/**
 * Utility functions for generating and sharing celebration & event guest invite links.
 */

export function getGuestLandingBaseUrl(): string {
  const configuredBaseUrl = import.meta.env.VITE_GUEST_LANDING_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:5437`;
  }

  return window.location.origin;
}

export interface InviteUrlOptions {
  eventId?: number;
  token?: string;
  preview?: boolean;
}

export function getAbsoluteInviteUrl(inviteUrl?: string | null): string {
  if (!inviteUrl?.trim()) {
    return '';
  }

  return new URL(inviteUrl.trim(), `${getGuestLandingBaseUrl()}/`).toString();
}

export function getCelebrationInviteUrl(
  slug?: string | null,
  options?: InviteUrlOptions
): string {
  if (!slug || !slug.trim()) {
    return '';
  }

  const baseUrl = `${getGuestLandingBaseUrl()}/g/${encodeURIComponent(slug.trim())}`;
  const params = new URLSearchParams();

  if (options?.eventId) {
    params.set('event', String(options.eventId));
  }
  if (options?.token) {
    params.set('token', options.token);
  }
  if (options?.preview) {
    params.set('preview', 'true');
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function shareInviteLink(
  channel: 'whatsapp' | 'sms' | 'email',
  title: string,
  url: string
): void {
  if (!url) return;

  const displayTitle = title || 'Our Celebration';
  const textMessage = `You're invited to ${displayTitle}! View details and RSVP here: ${url}`;
  const encodedText = encodeURIComponent(textMessage);

  if (channel === 'whatsapp') {
    window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
    return;
  }

  if (channel === 'sms') {
    window.location.href = `sms:?body=${encodedText}`;
    return;
  }

  const emailSubject = encodeURIComponent(`You're invited: ${displayTitle}`);
  const emailBody = encodeURIComponent(
    `You are warmly invited to ${displayTitle}.\n\nPlease open your invitation and RSVP using the link below:\n${url}\n\nWe look forward to celebrating together!`
  );
  window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
}
